import ParameterValidator from '@mvps-genesis/parameter-validator';
import {MenuBar, MenuItem} from '@mvps-genesis/accessibility';
import {getAbsoluteParent, getNextSibling, getParent} from '@mvps-genesis/selector-helpers';
import {createFocusTrap} from 'focus-trap';

/* eslint-disable no-invalid-this */
export default class NavigationMenu {

	/**
	 * Options:
	 *
	 * options.drawers
	 * options.mainMenu
	 * options.mainMenuOpenButtons
	 * options.mainMenuCloseButtons
	 * options.menuItems
	 * options.parentMenus
	 * options.showPreviousMenuButtons
	 */
	constructor (options) {

		// Create validator
		this._validator = new ParameterValidator(this);

		this.options = this._validator.isObject('options', options);
		this.breakpoint = this._validator.isString('options.breakpoint', options.breakpoint || '75em');

		this.elements = {
			mainMenu: this._validator.isElement('options.mainMenu', options.mainMenu),
			mainMenuCloseButtons: this._validator.isElementList('options.mainMenuCloseButtons', options.mainMenuCloseButtons),
			mainMenuOpenButtons: this._validator.isElementList('options.mainMenuOpenButtons', options.mainMenuOpenButtons),
			parentMenus: this._validator.isElementList('options.parentMenus', options.parentMenus),
			showPreviousMenuButtons: this._validator.isElementList('options.showPreviousMenuButtons', options.showPreviousMenuButtons)
		};
		this.elements.menuItems = this._validator.isElementList('options.menuItems', options.menuItems, {container: this.elements.mainMenu});
		this.elements.parentMenuItems = this._validator.isElementList('parentMenuItems', `${options.parentMenus} > ${options.menuItems}`, {container: this.elements.mainMenu});

		this.accessibility = {
			// Create focus trap for mobile menu
			focusTrap: createFocusTrap([this.elements.mainMenu, '.g-site-header', '#js-mini-basket'], {
				allowOutsideClick: (e) => Boolean(e.target.closest('[aria-hidden="false"]')),
				escapeDeactivates: false,
				setReturnFocus: this.elements.mainMenuOpenButtons[0]
			}),
			// Create accessible (keyboard navigation) menu from the main NavigationMenu element
			menuBar: new MenuBar({element: this.elements.mainMenu})
		};

		this.bodyElement = document.querySelector('body');
		this.menuWrapperElement = document.querySelector('.g-navigation-menu__wrap');
		this.siteNavigationElement = document.querySelector('.g-site-navigation');

		this.bindEvents();

	}

	isMobile () {
		return this.breakpoint ? window.matchMedia(`(max-width: ${this.breakpoint})`).matches : false;
	}

	/**
	 * Swap in the menu thumbnails on first use. They're full-size category images,
	 * so loading them with the page cost ~3 MB on desktop before anyone opened the menu.
	 *
	 * @returns {void}
	 */
	loadMenuImages () {
		if (this.menuImagesLoaded) {
			return;
		}
		this.menuImagesLoaded = true;

		for (const image of this.elements.mainMenu.querySelectorAll('img[data-menu-src]')) {
			image.src = image.dataset.menuSrc;
			image.removeAttribute('data-menu-src');
		}
	}

	bindEvents (add = true) {

		const method = add ? 'addEventListener' : 'removeEventListener';

		// Add listener for top-level close
		this.accessibility.menuBar[method](MenuBar.events.close, (e) => this.onAccessibilityMenuClose(e), true);

		// Mobile open and close events
		for (const mainMenuOpenButton of this.elements.mainMenuOpenButtons) {

			mainMenuOpenButton[method]('click', this.onMainMenuOpenButtonClick, false);

		}
		for (const mainMenuCloseButton of this.elements.mainMenuCloseButtons) {

			mainMenuCloseButton[method]('click', this.mainMenuClose, false);

		}

		for (const menuItem of this.elements.menuItems) {

			menuItem[method]('blur', this.onMenuItemBlur, false);
			menuItem[method]('focus', this.onMenuItemFocus, false);

		}

		for (const parentMenuItem of this.elements.parentMenuItems) {

			parentMenuItem[method]('click', this.onParentMenuItemClick, false);
			parentMenuItem[method](MenuBar.events.enterSubMenu, this.onParentMenuItemClick, false);

		}

		if (this.siteNavigationElement) {
			// Hover events
			this.siteNavigationElement[method]('mouseenter', this.onMenuItemFocus, false);
			this.siteNavigationElement[method]('mouseleave', this.onMenuItemBlur, false);
		}

		// Show previous button
		for (const showPreviousMenuButton of this.elements.showPreviousMenuButtons) {

			showPreviousMenuButton[method]('click', this.onPreviousButtonClick, false);

		}

	}

	destroyEvents () {
		this.bindEvents(false);
	}

	onMainMenuOpenButtonClick = () => {
		if (this.elements.mainMenu.classList.contains('is-open')) {
			this.mainMenuClose();
		} else {
			this.mainMenuOpen();
		}
	}

	mainMenuOpen = () => {
		this.loadMenuImages();
		document.documentElement.classList.add('has-open-main-menu');
		this.elements.mainMenu.classList.add('is-open');
		this.elements.mainMenu.classList.remove('is-hidden');

		this.accessibility.menuBar.focusFirstMenuItem();
		this.accessibility.focusTrap.activate();

	}

	mainMenuClose = () => {

		document.documentElement.classList.remove('has-open-main-menu');
		this.elements.mainMenu.classList.remove('is-open');
		this.elements.mainMenu.classList.add('is-hidden');

		this.accessibility.menuBar.resetMenuItemsState();
		this.accessibility.focusTrap.deactivate();

	}

	onAccessibilityMenuClose (e) {

		if (!this.isMobile()) {
			this.closeDesktopDrawer(e && e.detail && e.detail.menu);
			return false;
		}

		if (e && e.detail && e.detail.menu) {
			if (e.detail.menu.root) {
				this.mainMenuClose();
			}
			else {
				const previousButton = e.detail.menu.element.querySelector(this.options.showPreviousMenuButtons);
				this.showPreviousMenu(previousButton);
			}

		}

		return true;

	}

	/**
	 * Close the open desktop drawer on Escape. The menu library fires this after
	 * moving focus back to the top-level item, whose focus handler has already
	 * re-opened the drawer, so it is closed here instead.
	 *
	 * @param {Object} menu The Menu or MenuBar instance that closed.
	 * @returns {void}
	 */
	closeDesktopDrawer (menu) {
		// The menubar itself, or a drawer opened straight from one of its items
		const isTopLevel = menu === this.accessibility.menuBar || Boolean(menu && menu.parentMenuItem && menu.parentMenuItem === menu.parentMenuBarItem);

		// Escape inside a nested column leaves the mega-menu drawer open
		if (!isTopLevel) {
			return;
		}

		const parent = getAbsoluteParent(document.activeElement, this.options.parentMenus);

		if (parent) {
			parent.classList.remove('is-open');
		}

		if (this.bodyElement) {
			this.bodyElement.classList.remove('has-desktop-menu-open');
		}
	}

	onMenuItemFocus = (e) => {
		if (this.isMobile()) {
			return;
		}

		this.loadMenuImages();

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			parent.classList.add('is-open');
		}

		if (this.bodyElement) {
			this.bodyElement.classList.add('has-desktop-menu-open');
		}
	}

	onMenuItemBlur = (e) => {
		if (this.isMobile()) {
			return;
		}

		const parent = getAbsoluteParent(e.target, this.options.parentMenus);

		if (parent) {
			parent.classList.remove('is-open');
		}

		if (this.bodyElement) {
			this.bodyElement.classList.remove('has-desktop-menu-open');
		}
	}

	onParentMenuItemClick = (e) => {
		e.stopPropagation();

		if (!this.isMobile()) {
			return true;
		}

		e.preventDefault();

		let clickElement = e.target;
		const parentElement = getParent(clickElement, this.options.menuItems);
		if (parentElement) {
			clickElement = parentElement;
		}

		if (this.menuWrapperElement) {
			this.menuWrapperElement.scrollTo(0, 0);
			this.menuWrapperElement.classList.remove('overflow-auto');
		}

		const nextDrawer = getNextSibling(clickElement, this.options.drawers);
		if (nextDrawer) {
			nextDrawer.classList.add('is-open');
		}

		return false;

	}

	onPreviousButtonClick = (e) => {
		e.stopPropagation();
		e.preventDefault();

		const parentDrawer = e.target.closest(this.options.drawers);

		if (parentDrawer) {
			parentDrawer.classList.remove('is-open');
		}

		if (this.menuWrapperElement) {
			this.menuWrapperElement.scrollTo(0, 0);
			this.menuWrapperElement.classList.add('overflow-auto');
		}

		// Properly handle correct focusing on "back"
		const menuItem = MenuItem.getInstance(e.target);
		if (menuItem) {
			menuItem.dispatchEvent('keydown', {
				key: 'Escape'
			});
		}

	}

	showPreviousMenu (target) {
		const parentDrawer = getParent(target, this.options.drawers);
		if (parentDrawer) {
			parentDrawer.classList.remove('is-open');
		}
	}

};
